import { memo } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Camera, Droplets, Wind, Ruler, ShieldCheck } from 'lucide-react';
import { useSearchStore } from '../../store/useSearchStore';
import Photo from '../ui/Photo';
import { formatCentsCompact, formatDistance, formatFeet, gateHours } from '../../lib/format';
import { SPACE_TYPE_LABELS } from '../../lib/fit';
import { uploadUrl } from '../../lib/uploads';

/**
 * One search result, and the whole card is the link to it.
 *
 * It used to be an <article> whose click set selectedFacilityId for the map to
 * highlight, with only the title navigating anywhere. With the map gone that
 * click did nothing at all — a card that looks clickable, has a pointer
 * cursor, and swallows the click is worse than one that is plainly inert.
 *
 * An <a> around the whole card rather than a click handler: keyboard focus,
 * middle-click, cmd-click and "open in new tab" all come for free, and none of
 * them do with onClick + navigate.
 *
 * memo() is load-bearing: without it, hovering one card re-renders all of them.
 */
function FacilityCard({ facility }) {
  const setHovered = useSearchStore((s) => s.setHoveredFacility);
  const clearHovered = useSearchStore((s) => s.clearHoveredFacility);
  const isHovered = useSearchStore((s) => s.hoveredFacilityId === facility.public_id);

  return (
    <Link
      to={`/facilities/${facility.slug}`}
      aria-label={`${facility.name} in ${facility.city}, ${facility.state_code} — view spaces and prices`}
      onMouseEnter={() => setHovered(facility.public_id)}
      onMouseLeave={clearHovered}
      onFocus={() => setHovered(facility.public_id)}
      onBlur={clearHovered}
      className={`group block rounded-xl border bg-white p-3 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
        isHovered ? 'border-slate-300 shadow-card' : 'border-slate-200'
      }`}
    >
      <div className="flex gap-3">
        <Photo src={uploadUrl(facility.photos?.[0])} alt={facility.name}
               className="h-28 w-36 shrink-0 rounded-lg object-cover" loading="lazy" />

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="truncate text-sm font-bold text-slate-900 group-hover:underline">
                {facility.name}
              </h3>
              <p className="truncate text-xs text-slate-500">
                {facility.city}, {facility.state_code}
                {facility.distance_miles != null && <> · {formatDistance(facility.distance_miles)}</>}
              </p>
            </div>
            <div className="shrink-0 text-right">
              <p className="text-base font-extrabold text-slate-900">
                {formatCentsCompact(facility.min_rate_cents)}
              </p>
              {/* "and up" read as "the cheapest space here", which is false
                  whenever the rig rules out the cheaper ones. A yard with $80
                  spaces showing $125 looks like a pricing bug rather than a
                  fit result. Say which number this is. */}
              <p className="text-[12px] text-slate-500">/mo, cheapest that fits</p>
            </div>
          </div>

          {/* The number that actually matters: units that fit THIS vehicle. */}
          <p className={`mt-1.5 text-[12px] font-semibold ${
            facility.available_units > 0 ? 'text-brand-700' : 'text-slate-500'}`}>
            {facility.available_units > 0
              ? `${facility.available_units} space${facility.available_units === 1 ? '' : 's'} available that fit`
              : 'Full — no space free right now'}
            {facility.longest_unit_ft != null && (
              <span className="font-normal text-slate-500"> · up to {formatFeet(facility.longest_unit_ft)}</span>
            )}
          </p>

          <div className="mt-2 flex flex-wrap gap-1">
            {facility.space_types?.map((type) => (
              <span key={type} className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-700">
                {SPACE_TYPE_LABELS[type] ?? type}
              </span>
            ))}
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-slate-600">
            <Spec Icon={Clock} label={gateHours(facility)} />
            {facility.has_cameras && <Spec Icon={Camera} label="Cameras" />}
            {facility.has_perimeter_fence && <Spec Icon={ShieldCheck} label="Fenced" />}
            {facility.has_dump_station && <Spec Icon={Droplets} label="Dump" />}
            {facility.has_air_compressor && <Spec Icon={Wind} label="Air" />}
          </div>
        </div>
      </div>
    </Link>
  );
}

function Spec({ Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <Icon className="h-3 w-3 text-brand-500" aria-hidden="true" />
      {label}
    </span>
  );
}

export default memo(FacilityCard);
